import { motion } from "framer-motion";
import { TriangleAlert } from "lucide-react";
import { toneSurface } from "@/lib/mockData";
import { cn } from "@/lib/utils";

const TONE_TINTS = {
  dark: "bg-zinc-50",
  blue: "bg-[#EAF1FF]/55",
  neutral: "bg-zinc-50",
  lavender: "bg-[#F2EBFF]/55",
  mint: "bg-[#EAF7F0]/55",
  peach: "bg-[#FFF1E6]/55",
  sky: "bg-[#E8F4FA]/55",
  stone: "bg-[#F1EEE8]/60",
};

/**
 * Server-shaped chat message:
 *  { message_id, sender: "user"|"agent", msg_type: "text"|"advice"|"recommendation"|"warning", content }
 */
export default function MessageBubble({ msg, agent, index = 0 }) {
  const isUser = msg.sender === "user";
  const text = msg.content;

  if (isUser) {
    return (
      <motion.div
        data-testid={`msg-user-${msg.message_id}`}
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.22, delay: 0.02 * index }}
        className="flex justify-end"
      >
        <div className="max-w-[65%] rounded-2xl rounded-br-md bg-black px-4 py-2.5 text-[13.5px] leading-relaxed text-white shadow-sm">
          {text}
        </div>
      </motion.div>
    );
  }

  // Agent / system bubble
  const rawType = msg.msg_type ?? "advice";
  // System messages render as plain advice-styled bubbles.
  const type = ["advice", "recommendation", "warning"].includes(rawType)
    ? rawType
    : "advice";
  const isDarkAvatar = agent.tone === "dark";
  const tone = toneSurface[agent.tone] ?? toneSurface.neutral;

  const wrapperClass = cn(
    "max-w-[72%] rounded-2xl px-4 py-3 text-[13.5px] leading-relaxed whitespace-pre-wrap",
    type === "advice" &&
      "border border-black/[0.04] bg-white text-zinc-800 shadow-[0_1px_0_rgba(0,0,0,0.02)]",
    type === "recommendation" &&
      cn(
        TONE_TINTS[agent.tone] ?? TONE_TINTS.neutral,
        "border border-black/[0.04] text-zinc-800",
      ),
    type === "warning" &&
      "border-l-2 border-l-orange-500 border-y border-r border-y-orange-100 border-r-orange-100 bg-[#FFF5F0] text-orange-950",
  );

  return (
    <motion.div
      data-testid={`msg-agent-${msg.message_id}`}
      data-msg-type={type}
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.22, delay: 0.02 * index }}
      className="flex items-start gap-3"
    >
      <div
        className={cn(
          "flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-[12px] font-extrabold",
          isDarkAvatar
            ? "bg-[#0B0B0E] text-white"
            : cn(tone.bg, "text-zinc-900"),
        )}
        style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.04)" }}
      >
        {agent.name[0]}
      </div>
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="mb-1 flex items-center gap-2">
          <span className="text-[13px] font-bold text-zinc-900">
            {agent.name}
          </span>
          <span className="text-[10px] uppercase tracking-[0.14em] text-zinc-400">
            {agent.role}
          </span>
        </div>
        <div className={wrapperClass}>
          {type === "recommendation" && (
            <div className="eyebrow mb-1.5 text-[10px] text-zinc-700">
              Recommendation
            </div>
          )}
          {type === "warning" && (
            <div className="mb-1.5 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.18em] text-orange-700">
              <TriangleAlert className="h-3 w-3" strokeWidth={2.6} /> Warning
            </div>
          )}
          {text}
        </div>
      </div>
    </motion.div>
  );
}
